import { useEffect, useRef, type MouseEvent, type ReactNode } from "react";
import { useLocation } from "wouter";
import { CalendarDays, ClipboardCheck, ClipboardList, FileText, Gauge, LayoutDashboard, ListTree, Settings2 } from "lucide-react";
import { rememberPlan } from "../lib/nav.tsx";
import { SkipLink, TopBar } from "./shell.tsx";
import { ErrorState, Skeleton, cx } from "./ui.tsx";
import { PlanProvider, usePlanAccessListener, usePlanQuery } from "../lib/plan.tsx";
import { useGuardedNavigate } from "../lib/guard.tsx";
import { ApiError } from "../lib/api.ts";
import { DIMENSION_LABEL, roleLabel } from "../lib/types.ts";
import { NotFoundPage } from "../pages/NotFoundPage.tsx";
import { Credits } from "./brand.tsx";
import { logoUrl } from "./institution-logo.tsx";
import { useMaturityInstitutions, useWorkshopDiagnosis, withInstitution } from "../lib/maturity.ts";

/** In-app link that respects the unsaved-change guard. */
export function GuardedLink({ href, className, children, ...rest }: { href: string; className?: string; children: ReactNode; "aria-current"?: "page" | undefined; "aria-label"?: string }) {
  const go = useGuardedNavigate();
  return (
    <a
      href={href}
      className={className}
      {...rest}
      onClick={(e: MouseEvent) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        go(href);
      }}
    >
      {children}
    </a>
  );
}

const NAV = [
  { slug: "painel", label: "Monitoramento", Icon: LayoutDashboard },
  { slug: "planejamento", label: "Planejamento", Icon: ListTree },
  { slug: "agenda", label: "Agenda", Icon: CalendarDays },
  { slug: "relatorios", label: "Relatórios", Icon: FileText },
  { slug: "revisoes", label: "Revisões", Icon: ClipboardCheck, instructor: true },
  { slug: "configuracoes", label: "Configurações", Icon: Settings2, instructor: true },
];

export function PlanShell({ planId, children }: { planId: string; children: ReactNode }) {
  usePlanAccessListener(planId);
  const q = usePlanQuery(planId);
  const maturity = useMaturityInstitutions();
  const assigned = useWorkshopDiagnosis(planId);
  const [location] = useLocation();
  const navRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (q.data) rememberPlan(planId);
  }, [q.data, planId]);
  // Keep the active section visible in the horizontal phone nav (UX-08).
  useEffect(() => {
    navRef.current?.querySelector<HTMLElement>("[aria-current=page]")?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [location, q.data]);

  if (q.isPending) {
    return (
      <div className="min-h-dvh">
        <TopBar />
        <div className="mx-auto max-w-5xl px-4 py-8"><Skeleton lines={5} /></div>
      </div>
    );
  }
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.status === 404) return <NotFoundPage />;
    return (
      <div className="min-h-dvh">
        <TopBar />
        <div className="mx-auto max-w-5xl px-4 py-8"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>
      </div>
    );
  }
  const plan = q.data;
  const items = NAV.filter((n) => !n.instructor || plan.capabilities.can_manage);
  const active = (slug: string) => location.startsWith(`/planos/${planId}/${slug}`) ||
    (slug === "planejamento" && location.startsWith(`/planos/${planId}/iniciativas`));
  const link = (n: (typeof NAV)[number]) => (
    <li key={n.slug} className="shrink-0">
      <GuardedLink
        href={`/planos/${planId}/${n.slug}`}
        aria-current={active(n.slug) ? "page" : undefined}
        className={cx(
          "flex min-h-10 items-center gap-2.5 rounded-ctl px-3 py-2 text-sm whitespace-nowrap transition-[color,background-color,box-shadow] duration-[var(--duration-base)]",
          active(n.slug) ? "bg-surface font-semibold text-primary shadow-e1 ring-1 ring-line [&>svg]:text-accent" : "elevate text-muted hover:text-ink",
        )}
      >
        <n.Icon className="size-4 shrink-0" aria-hidden />
        {n.label}
      </GuardedLink>
    </li>
  );

  return (
    <PlanProvider plan={plan}>
      <SkipLink />
      <div className="flex min-h-dvh flex-col">
        <TopBar plan={{ id: plan.id, name: plan.name }} />
        <div className="flex flex-1 flex-col md:flex-row">
          {/* The column carries the background for the full page height; the nav inside stays in view. */}
          <div className="border-b border-line bg-side md:w-56 md:shrink-0 md:border-r md:border-b-0">
          <nav aria-label="Seções da oficina" className="md:sticky md:top-16 md:max-h-[calc(100dvh-4rem)] md:overflow-y-auto">
            <div className="hidden px-4 pt-5 pb-3 md:block">
              {logoUrl(plan) && <img src={logoUrl(plan)!} alt={`Logomarca de ${plan.institution.name}`} className="mb-3 max-h-11 max-w-full object-contain object-left" />}
              <p className="eyebrow">{plan.institution.name}</p>
              <p className="mt-1 text-sm leading-snug font-semibold text-ink">{plan.name}</p>
              <p className="mt-1 text-xs text-muted">{DIMENSION_LABEL} · {roleLabel[plan.membership.role]}</p>
            </div>
            <ul ref={navRef} className="scroll-fade-x flex gap-1 overflow-x-auto px-3 py-2 md:flex-col md:overflow-visible md:[mask-image:none] md:py-1">
              {items.filter((n) => !n.instructor).map(link)}
              {/* BLD-28: maturity categories this member answers or validates. */}
              {(assigned.data?.length ?? 0) > 0 && link({ slug: "diagnostico", label: "Diagnóstico", Icon: ClipboardList })}
              {plan.capabilities.can_manage && (
                <li className="eyebrow hidden px-3 pt-5 pb-1 md:block" aria-hidden>Instrutor</li>
              )}
              {items.filter((n) => n.instructor).map(link)}
              {/* BLD-26: the institution's maturity module, for instructors of this institution (no plan scope). */}
              {plan.capabilities.can_manage && maturity.data?.some((i) => i.id === plan.institution.id) && (
                <li className="shrink-0">
                  <GuardedLink href={withInstitution("/diagnosticos", plan.institution.id)}
                    className="elevate flex min-h-10 items-center gap-2.5 rounded-ctl px-3 py-2 text-sm whitespace-nowrap text-muted hover:text-ink">
                    <Gauge className="size-4 shrink-0" aria-hidden />
                    Diagnóstico institucional
                  </GuardedLink>
                </li>
              )}
            </ul>
          </nav>
          </div>
          <main id="conteudo" className="min-w-0 flex-1 px-4 py-6 sm:px-7">
            <div className="mx-auto w-full max-w-[1120px]">{children}</div>
            <Credits className="mt-12 border-t border-line pt-4" />
          </main>
        </div>
      </div>
    </PlanProvider>
  );
}
